/**
 * Integration Card Component
 *
 * Reusable card component for displaying integration providers.
 */

import { ExternalLink } from "lucide-react";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Integration } from "@/types/integrations";
import { cn } from "@/lib/utils";

interface IntegrationCardProps {
  integration: Integration;
  configuredCount?: number;
  onConfigure?: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

export function IntegrationCard({
  integration,
  configuredCount = 0,
  onConfigure,
  disabled,
  disabledReason,
}: IntegrationCardProps) {
  const isActive = integration.status === "active";
  const isComingSoon = integration.status === "coming_soon";
  const isDisabled = disabled || isComingSoon || integration.status === "disabled";
  const isConfigured = isActive && configuredCount > 0;

  const getStatusBadge = () => {
    if (isComingSoon) {
      return (
        <Badge variant="secondary" className="text-xs">
          Coming Soon
        </Badge>
      );
    }
    if (isConfigured) {
      return (
        <Badge className="bg-green-600 hover:bg-green-700 text-xs">
          {configuredCount} Connected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs">
        Not Connected
      </Badge>
    );
  };

  const getButtonLabel = () => {
    if (isComingSoon) return "Coming Soon";
    if (isConfigured) return "Manage";
    return "Connect";
  };

  const cardContent = (
    <Card
      className={cn(
        "relative flex h-full flex-col rounded-[15px] shadow-none transition-all duration-200",
        isActive && !isDisabled && "hover:shadow-lg hover:border-primary/50 cursor-pointer",
        isDisabled && "opacity-60"
      )}
      onClick={isActive && !isDisabled && onConfigure ? onConfigure : undefined}
    >
      <CardHeader className="flex-1 pb-4">
        {/* Logo and Badge Row */}
        <div className="flex items-center justify-between mb-3">
          {integration.logo && (
            <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-muted/40">
              <integration.logo className="w-8 h-8" />
            </div>
          )}
          {getStatusBadge()}
        </div>

        {/* Title Row */}
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-medium">{integration.name}</CardTitle>
          {integration.docsUrl && (
            <a
              href={integration.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open ${integration.name} documentation (opens in new tab)`}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>

        {/* Description */}
        <CardDescription className="mt-2 text-sm leading-relaxed line-clamp-3">
          {integration.description}
        </CardDescription>
      </CardHeader>

      <CardFooter className="pt-0">
        <Button
          variant={isConfigured ? "default" : "outline"}
          className="w-full rounded-full"
          disabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            if (isActive && !isDisabled && onConfigure) {
              onConfigure();
            }
          }}
        >
          {getButtonLabel()}
        </Button>
      </CardFooter>
    </Card>
  );

  if (isDisabled) {
    const tooltipMessage = isComingSoon
      ? "This integration is under development. Contact support for early access."
      : disabledReason || "You don't have permission to configure this integration.";
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltipMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardContent;
}
